import { useState, useMemo } from "react";
import { useGetMentorForecastingData } from "@/lib/api/admin";
import {
  ForecastSettings,
  MonthData,
  ChartDataPoint,
  ActualMonthData,
  YearlyData,
} from "@/components/mentor-forecasting/types";
import {
  DEFAULT_SETTINGS,
  STORAGE_KEY,
  TEEN_FORECAST_STORAGE_KEY,
  MENTORS_LEAVING_STORAGE_KEY,
  MONTHS,
  AVAILABLE_YEARS,
} from "@/components/mentor-forecasting/constants";

const getFromStorage = <T>(key: string, defaultValue: T): T => {
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
};

const createEmptyYearlyData = (): YearlyData<number> => 
  Object.fromEntries(AVAILABLE_YEARS.map(year => [year, {}]));

export function useForecastData() {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const [settings, setSettings] = useState<ForecastSettings>(() =>
    getFromStorage(STORAGE_KEY, DEFAULT_SETTINGS)
  );

  const [teenForecastByYear, setTeenForecastByYear] = useState<YearlyData<number>>(() =>
    getFromStorage(TEEN_FORECAST_STORAGE_KEY, createEmptyYearlyData())
  );

  const [mentorsLeavingByYear, setMentorsLeavingByYear] = useState<YearlyData<number>>(() =>
    getFromStorage(MENTORS_LEAVING_STORAGE_KEY, createEmptyYearlyData())
  );

  const { data: apiData, isLoading } = useGetMentorForecastingData();

  const updateSettings = (newSettings: Partial<ForecastSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const actualDataByYear = useMemo(() => {
    const result: YearlyData<ActualMonthData> = Object.fromEntries(
      AVAILABLE_YEARS.map(year => [year, {}])
    );

    if (apiData) {
      apiData.forEach((item) => {
        const date = new Date(item.month_end);
        const year = date.getFullYear();
        const month = date.getMonth();

        if (!result[year]) {
          result[year] = {};
        }

        result[year][month] = {
          teens: item.total_teens,
          mentors: item.total_mentors - item.total_lead_mentors,
          leads: item.total_lead_mentors,
          supervisors: item.total_supervisors,
          deactivatedMentors: item.deactivated_mentors,
        };
      });
    }

    return result;
  }, [apiData]);

  const teenForecast = teenForecastByYear[selectedYear] || {};
  const mentorsLeaving = mentorsLeavingByYear[selectedYear] || {};
  const actualData = actualDataByYear[selectedYear] || {};
  const prevYearActual = actualDataByYear[selectedYear - 1];
  const prevYearForecast = teenForecastByYear[selectedYear - 1];

  const idealCaseload = useMemo(
    () => settings.maxCaseload * (1 - settings.bufferPercent / 100),
    [settings.maxCaseload, settings.bufferPercent]
  );

  const prevYearDecemberTeens = useMemo(() => {
    const growthMultiplier = 1 + settings.growthPercent / 100;
    if (prevYearForecast && prevYearForecast[11] !== undefined) {
      return prevYearForecast[11];
    }
    if (prevYearActual && prevYearActual[11]?.teens) {
      return prevYearActual[11].teens;
    }
    if (prevYearActual && prevYearActual[10]?.teens) {
      return Math.round(prevYearActual[10].teens * growthMultiplier);
    }
    return 0;
  }, [prevYearActual, prevYearForecast, settings.growthPercent]);

  const prevYearDecemberStaff = useMemo(() => {
    if (prevYearActual && prevYearActual[11]) {
      return {
        mentors: prevYearActual[11].mentors,
        leads: prevYearActual[11].leads,
        supervisors: prevYearActual[11].supervisors,
      };
    }

    let decemberTeens = 0;
    if (prevYearForecast && prevYearForecast[11] !== undefined) {
      decemberTeens = prevYearForecast[11];
    } else if (prevYearActual && prevYearActual[10]?.teens) {
      const growthMultiplier = 1 + settings.growthPercent / 100;
      decemberTeens = Math.round(prevYearActual[10].teens * growthMultiplier);
    }

    const idealMentors = Math.ceil(decemberTeens / idealCaseload);
    return {
      mentors: idealMentors,
      leads: Math.ceil(idealMentors / settings.mentorsPerLead),
      supervisors: Math.ceil(idealMentors / settings.mentorsPerSupervisor),
    };
  }, [prevYearActual, prevYearForecast, idealCaseload, settings]);

  const forecastData = useMemo<MonthData[]>(() => {
    const growthMultiplier = 1 + settings.growthPercent / 100;
    const results: MonthData[] = [];

    for (let index = 0; index < MONTHS.length; index++) {
      const month = MONTHS[index];
      const isPast = selectedYear < currentYear || (selectedYear === currentYear && index < currentMonth);
      const actual = actualData[index];

      let forecastedTeens: number;
      if (isPast && actual?.teens) {
        forecastedTeens = actual.teens;
      } else if (teenForecast[index] !== undefined) {
        forecastedTeens = teenForecast[index];
      } else if (!isPast && actual?.teens) {
        forecastedTeens = actual.teens;
      } else if (index > 0 && results[index - 1]) {
        forecastedTeens = Math.round(results[index - 1].forecastedTeens * growthMultiplier);
      } else if (index === 0 && prevYearDecemberTeens > 0) {
        forecastedTeens = Math.round(prevYearDecemberTeens * growthMultiplier);
      } else {
        forecastedTeens = 0;
      }

      const minimalMentors = Math.ceil(forecastedTeens / settings.maxCaseload);
      const idealMentors = Math.ceil(forecastedTeens / idealCaseload);
      const minimalLeads = Math.ceil(minimalMentors / settings.mentorsPerLead);
      const idealLeads = Math.ceil(idealMentors / settings.mentorsPerLead);
      const minimalSupervisors = Math.ceil(minimalMentors / settings.mentorsPerSupervisor);
      const idealSupervisors = Math.ceil(idealMentors / settings.mentorsPerSupervisor);

      results.push({
        month,
        monthIndex: index,
        forecastedTeens,
        actualTeens: actual?.teens,
        minimalMentors,
        idealMentors,
        actualMentors: actual?.mentors,
        minimalLeads,
        idealLeads,
        actualLeads: actual?.leads,
        minimalSupervisors,
        idealSupervisors,
        actualSupervisors: actual?.supervisors,
        isPast,
        isCurrent: selectedYear === currentYear && index === currentMonth,
      });
    }
    return results;
  }, [teenForecast, actualData, prevYearDecemberTeens, settings, idealCaseload, currentMonth, currentYear, selectedYear]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    const isCurrentMonth = (index: number) =>
      selectedYear === currentYear && index === currentMonth;
    const isPastMonth = (index: number) =>
      selectedYear < currentYear || (selectedYear === currentYear && index < currentMonth);
    const isPrevMonth = (index: number) =>
      selectedYear === currentYear && index === currentMonth - 1;

    return forecastData.map((data, index) => {
      const past = isPastMonth(index);
      const current = isCurrentMonth(index);
      const prev = isPrevMonth(index);

      return {
        month: data.month,
        minimum: data.minimalMentors,
        ideal: data.idealMentors,
        actualPast: past && data.actualMentors ? data.actualMentors : undefined,
        actualCurrent: (current || prev) && data.actualMentors ? data.actualMentors : undefined,
        actual: data.actualMentors,
        forecastedTeens: data.forecastedTeens,
        actualTeens: data.actualTeens,
        minimumLeads: data.minimalLeads,
        idealLeads: data.idealLeads,
        actualLeads: data.actualLeads,
        minimumSupervisors: data.minimalSupervisors,
        idealSupervisors: data.idealSupervisors,
        actualSupervisors: data.actualSupervisors,
        isPast: data.isPast,
      };
    });
  }, [forecastData, selectedYear, currentYear, currentMonth]);

  const handleTeenForecastChange = (monthIndex: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setTeenForecastByYear((prev) => {
      const currentYearData = { ...prev[selectedYear] };
      currentYearData[monthIndex] = numValue;
      for (let i = monthIndex + 1; i < 12; i++) {
        delete currentYearData[i];
      }
      const updated = { ...prev, [selectedYear]: currentYearData };
      localStorage.setItem(TEEN_FORECAST_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const handleMentorsLeavingChange = (monthIndex: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setMentorsLeavingByYear((prev) => {
      const currentYearData = { ...prev[selectedYear] };
      currentYearData[monthIndex] = numValue;
      const updated = { ...prev, [selectedYear]: currentYearData };
      localStorage.setItem(MENTORS_LEAVING_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const clearForecasts = () => {
    const emptyForecasts = createEmptyYearlyData();
    setTeenForecastByYear(emptyForecasts);
    localStorage.setItem(TEEN_FORECAST_STORAGE_KEY, JSON.stringify(emptyForecasts));
    setMentorsLeavingByYear(emptyForecasts);
    localStorage.setItem(MENTORS_LEAVING_STORAGE_KEY, JSON.stringify(emptyForecasts));
  };

  const toggleRowExpanded = (rowKey: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  return {
    // State
    selectedYear,
    setSelectedYear,
    settings,
    updateSettings,
    expandedRows,
    toggleRowExpanded,
    isLoading,
    
    // Data
    forecastData,
    chartData,
    actualData,
    actualDataByYear,
    teenForecast,
    mentorsLeaving,
    mentorsLeavingByYear,
    prevYearDecemberStaff,
    
    // Handlers
    handleTeenForecastChange,
    handleMentorsLeavingChange,
    clearForecasts,
    
    // Computed values
    currentMonth,
    currentYear,
  };
}
